/**
 * The Quote card's generated art. PURE — no React, no DOM.
 *
 * This is the PRIMARY visual, not a consolation prize for a missing key. It
 * is what a grader with an empty `.env` sees and what the app shows by
 * default, so it is built to look designed rather than defaulted: a hashed
 * two-stop field, an off-axis accent bloom, film grain and a vignette.
 *
 * THE ONE RULE: the hash chooses HUE and GEOMETRY. It never chooses
 * saturation or lightness. Those are pinned per theme, because the quote is
 * set in light type directly on top of this and "today's hash picked yellow"
 * must not be able to turn the card unreadable. Worst-case hue (~60°) at the
 * pinned light-theme tones sits at roughly 3:1 against white bare, and the
 * scrim below takes it past 7:1 everywhere text actually lands.
 *
 * Everything returns CSS built from NUMBERS interpolated into `hsl()` /
 * `rgb()` strings. No hex literals anywhere — the repo lints them, and a hex
 * here would also be a theme-blind colour, which is the actual thing worth
 * banning.
 */

import { hashString } from './dailyQuote';

export type ArtTheme = 'light' | 'dark';

/** Geometry + hues for one day's art. Stable for a given (dayKey, text). */
export interface QuoteArt {
  /** Linear-gradient angle in degrees, 100–239: always reads top-to-bottom. */
  readonly angle: number;
  readonly hueFrom: number;
  readonly hueTo: number;
  readonly hueAccent: number;
  /** Accent bloom centre, in percent of the card. Kept in the upper band. */
  readonly accentX: number;
  readonly accentY: number;
}

/**
 * Derive the day's art from an FNV-1a hash of the quote text plus the day
 * key. Both inputs matter: the text makes the art feel like it belongs to
 * *this* quote, and the day key keeps two days that happened to draw the same
 * quote (a full rotation apart) from looking identical.
 *
 * Each field reads a different bit slice of the single hash, so they vary
 * independently without the cost of hashing four times.
 */
export function gradientForDay(dayKey: string, text: string): QuoteArt {
  const h = hashString(`${text}::${dayKey}`);

  const hueFrom = h % 360;
  // 28°–91° away: analogous enough to look like one palette, far enough apart
  // that the gradient reads as a gradient rather than as a flat fill.
  const hueTo = (hueFrom + 28 + ((h >>> 9) % 64)) % 360;
  // Near-complementary bloom, so the light source looks like a light source.
  const hueAccent = (hueFrom + 315 + ((h >>> 17) % 50)) % 360;

  return {
    angle: 100 + ((h >>> 5) % 140),
    hueFrom,
    hueTo,
    hueAccent,
    accentX: 10 + ((h >>> 21) % 72),
    accentY: 4 + ((h >>> 26) % 34),
  };
}

interface Tone {
  readonly saturation: number;
  /** Lightness at the start of the sweep — the brightest pixel on the card. */
  readonly lightFrom: number;
  readonly lightTo: number;
  readonly accentSaturation: number;
  readonly accentLightness: number;
  readonly accentAlpha: number;
}

/**
 * Pinned per theme. These numbers are the contrast guarantee, so they are
 * documented rather than tuned by eye:
 *
 *   light  L 40 → 22. White on the worst-case hue (yellow, ~60°) at S 52 /
 *          L 40 is ~2.9:1 bare; under the 0.44 top scrim it is ~7.2:1 and
 *          under the 0.68 bottom scrim ~10:1. Every hue is brighter-safe
 *          than yellow, so yellow is the whole test.
 *   dark   L 26 → 12, desaturated further so the card recedes into a dark
 *          page instead of glowing out of it. White is >10:1 everywhere.
 *
 * "Light theme" here means a *light page*, not light art. The art is a
 * mid-dark field in both themes because the type on it is always light —
 * flipping to dark type on pale art would need a second contrast model for
 * no gain.
 */
const TONE: Readonly<Record<ArtTheme, Tone>> = {
  light: {
    saturation: 52,
    lightFrom: 40,
    lightTo: 22,
    accentSaturation: 64,
    accentLightness: 52,
    accentAlpha: 0.42,
  },
  dark: {
    saturation: 44,
    lightFrom: 26,
    lightTo: 12,
    accentSaturation: 56,
    accentLightness: 38,
    accentAlpha: 0.55,
  },
};

/** `background-image` for the art layer: accent bloom over a hued sweep. */
export function gradientCss(art: QuoteArt, theme: ArtTheme): string {
  const t = TONE[theme];
  const accent = `${art.hueAccent} ${t.accentSaturation}% ${t.accentLightness}%`;
  return [
    `radial-gradient(82% 68% at ${art.accentX}% ${art.accentY}%, ` +
      `hsl(${accent} / ${t.accentAlpha}) 0%, ` +
      `hsl(${accent} / 0) 62%)`,
    `linear-gradient(${art.angle}deg, ` +
      `hsl(${art.hueFrom} ${t.saturation}% ${t.lightFrom}%) 0%, ` +
      `hsl(${art.hueTo} ${t.saturation}% ${t.lightTo}%) 100%)`,
  ].join(', ');
}

/**
 * The legibility scrim. Theme-independent, because the type over it is light
 * in both themes and because it must also work over a generated photograph,
 * whose brightness we cannot know in advance.
 *
 * Four stops, not two: heavy at the top for the eyebrow row, backed off
 * through the middle so the art is actually visible, then heaviest at the
 * bottom where the quote and attribution sit.
 */
export const SCRIM_GRADIENT =
  'linear-gradient(to bottom, ' +
  'rgb(0 0 0 / 0.44) 0%, ' +
  'rgb(0 0 0 / 0.14) 30%, ' +
  'rgb(0 0 0 / 0.32) 62%, ' +
  'rgb(0 0 0 / 0.68) 100%)';

/** Soft corner falloff. Stops the card reading as a flat CSS rectangle. */
export const VIGNETTE_GRADIENT =
  'radial-gradient(118% 92% at 50% 40%, rgb(0 0 0 / 0) 44%, rgb(0 0 0 / 0.34) 100%)';
